import { isBodyWorn, liveStateOf, minutesToHHMM, } from './index.js';
export const DEFAULT_THRESHOLDS = {
    // A 15 minute pack will not survive a scene; 45 gives a stage manager time
    // to prepare a swap at the next natural break rather than in a hurry.
    batteryCriticalMin: 15,
    batteryLowMin: 45,
    batteryCriticalBars: 1,
    batteryLowBars: 2,
    rfLowDbm: -85,
    audioSilentDb: -55,
    audioClipDb: -2,
    staleAfterSec: 12,
    dropoutDbm: -95,
    dropoutsWorthMentioning: 3,
    antennaImbalanceRatio: 0.1,
    antennaMinSamples: 60,
    // Under three minutes is not a swap, it is a scramble.
    minSwapWindowMin: 3,
};
const SEVERITY_RANK = {
    critical: 0, warning: 1, info: 2,
};
/** Match a reading to a channel by frequency, which is what the receiver knows. */
function channelFor(reading, channels) {
    if (reading.frequency === undefined)
        return undefined;
    return channels.find(c => c.frequency !== undefined && Math.abs(c.frequency - reading.frequency) < 0.0005);
}
/**
 * When you could actually get at this pack.
 *
 * Naming a shortfall is only half an answer -- "20 minutes short" still leaves
 * someone working out when they can do anything about it, usually while the
 * show is running. The channel's own occupancy already says: the gaps between
 * its assignments are exactly the moments that transmitter is off a body.
 *
 * Returns the first gap that starts before the battery gives out and is long
 * enough to be a swap rather than a scramble, or null when there is not one.
 */
export function findSwapWindow(channelId, assignments, now, runtimeLeftMin, minWindow) {
    const onThis = assignments
        .filter(a => a.channelId === channelId && a.scheduled)
        .sort((x, y) => x.start - y.start);
    if (onThis.length === 0)
        return null;
    // Merge overlapping bookings so a shared channel reads as one block of use.
    const busy = [];
    for (const a of onThis) {
        const last = busy.at(-1);
        if (last && a.start <= last.end)
            last.end = Math.max(last.end, a.end);
        else
            busy.push({ start: a.start, end: a.end });
    }
    const deadline = runtimeLeftMin === undefined ? Infinity : now + runtimeLeftMin;
    for (let i = 0; i < busy.length; i++) {
        const gapStart = busy[i].end;
        // The end of the day counts: after the last cue the pack is free.
        const gapEnd = busy[i + 1]?.start ?? 24 * 60;
        if (gapEnd - gapStart < minWindow)
            continue;
        if (gapStart < now)
            continue;
        if (gapStart > deadline)
            return null; // the battery goes first
        return { start: gapStart, end: Math.min(gapEnd, gapStart + 60) };
    }
    return null;
}
/** How the receiver reports which aerial is doing the work. */
function antennaSides(raw) {
    // Shure reports a two-character state -- AX, XB, AB, XX -- and some models
    // just say A or B. Both read the same way: is that letter present.
    const v = raw.toUpperCase();
    if (v.length >= 2)
        return { a: v[0] === 'A', b: v[1] === 'B' };
    return { a: v === 'A', b: v === 'B' };
}
export function countAntenna(raw) {
    if (!raw)
        return { a: 0, b: 0 };
    const { a, b } = antennaSides(raw);
    return { a: a ? 1 : 0, b: b ? 1 : 0 };
}
export function evaluateAlerts(input) {
    const th = input.thresholds ?? DEFAULT_THRESHOLDS;
    const byId = new Map(input.presenters.map(p => [p.id, p]));
    const history = input.history ?? {};
    const out = [];
    const matched = new Set();
    /*
     * Frequencies something is actually reporting on, kept alongside the matched
     * channel ids. A reading can only be attributed to one channel, so where a
     * plot plants two channels on one frequency -- a coordination mistake, but a
     * real one -- the second would never be matched and would be announced as
     * unreceived while a receiver sat plainly on it.
     */
    const heard = new Set();
    for (const r of input.telemetry) {
        const channel = channelFor(r, input.channels);
        const chNum = channel?.number;
        const where = chNum !== undefined ? `TX ${chNum}` : `${r.receiver} ch ${r.channel}`;
        // Who is on this channel now, and until when.
        const live = channel
            ? input.assignments.find(a => a.channelId === channel.id && liveStateOf(a, input.now) === 'live')
            : undefined;
        const held = channel && !live
            ? input.assignments.find(a => a.channelId === channel.id && liveStateOf(a, input.now) === 'held')
            : undefined;
        /*
         * The schedule is a plan; the receiver is the truth.
         *
         * Deciding "is anyone wearing this" from the clock alone fails in every
         * situation where a show is not running exactly to its own schedule -- a
         * late curtain, an afternoon tech, a rehearsal, a running order that has
         * slipped twenty minutes. In all of those the clock says nobody is on,
         * severity gets quietly downgraded, and the muted-mic and dead-element
         * checks stop firing entirely. That is precisely when someone needs them.
         *
         * A powered transmitter is evidence. If a channel is plotted to somebody
         * and the receiver reports RF coming off their pack, the mic is in use
         * whatever the running order believes.
         */
        const plotted = channel
            ? input.assignments.find(a => a.channelId === channel.id)
            : undefined;
        const transmitting = r.rfLevel !== undefined && r.rfLevel > th.dropoutDbm;
        const onAir = live ?? held ?? (transmitting ? plotted : undefined);
        const who = onAir ? byId.get(onAir.presenterId)?.name : undefined;
        const nameFor = who ? `${where} (${who})` : where;
        /*
         * A failing pack nobody is wearing is not an emergency. Severity is
         * whether it is on someone right now, so the one warning that matters is
         * not buried under five that do not.
         */
        const bump = (s) => onAir ? s : (s === 'critical' ? 'warning' : 'info');
        // Say so when the rig and the running order disagree, rather than quietly
        // acting on one of them.
        const offSchedule = !live && !held && !!onAir;
        const push = (kind, severity, message, advice) => out.push({
            kind, severity, message, advice,
            channelId: channel?.id, channelNumber: chNum, presenterName: who,
            key: `${r.receiver}:${r.channel}:${kind}`,
        });
        if (channel)
            matched.add(channel.id);
        if (r.frequency !== undefined)
            heard.add(r.frequency.toFixed(3));
        const hist = history[`${r.receiver}:${r.channel}`];
        /*
         * A transmitter on a frequency the plot does not know about. It cannot be
         * matched to a channel, so nothing else here applies to it -- and left
         * unsaid it would simply be invisible, which is the worst outcome for a
         * receiver somebody retuned by hand.
         */
        if (!channel && r.frequency !== undefined && input.channels.length > 0) {
            push('frequency-unplotted', 'warning', `${r.receiver} ch ${r.channel} is on ${r.frequency.toFixed(3)} MHz, which is not in the plot`, 'Either add it to the plot or retune the receiver to the coordinated frequency.');
            continue;
        }
        // -- reachability ------------------------------------------------------
        const ageSec = (input.nowMs - r.updatedAt) / 1000;
        if (ageSec > th.staleAfterSec) {
            push('telemetry-stale', bump('critical'), `${nameFor}: no reading for ${Math.round(ageSec)}s — receiver or bridge may be down`);
            // Everything below is derived from numbers we can no longer trust.
            continue;
        }
        // -- battery -----------------------------------------------------------
        const mins = r.batteryMinutes;
        const bars = r.batteryBars;
        const batteryCritical = (mins !== undefined && mins <= th.batteryCriticalMin)
            || (mins === undefined && bars !== undefined && bars <= th.batteryCriticalBars);
        const batteryLow = !batteryCritical && ((mins !== undefined && mins <= th.batteryLowMin)
            || (mins === undefined && bars !== undefined && bars <= th.batteryLowBars));
        /*
         * The swap window belongs on the warning, not on the emergency.
         *
         * By the time a pack cannot survive the stint it is already in, the first
         * gap on that channel is after it dies -- the gap *is* the end of the
         * stint. So naming a window is only ever useful earlier, while the battery
         * still reaches one. Down at critical the honest advice is different, and
         * shorter.
         */
        const swapWindow = channel
            ? findSwapWindow(channel.id, input.assignments, input.now, mins, th.minSwapWindowMin)
            : null;
        const swapAdvice = swapWindow
            ? `${who ?? 'It'} is off between ${minutesToHHMM(swapWindow.start)}`
                + ` and ${minutesToHHMM(swapWindow.end)} — swap it then.`
            : undefined;
        if (batteryCritical) {
            push('battery-critical', bump('critical'), `${nameFor}: battery ${mins !== undefined ? `${mins} min` : `${bars} bars`} — change it now`, swapAdvice);
        }
        else if (batteryLow) {
            push('battery-low', bump('warning'), `${nameFor}: battery ${mins !== undefined ? `${mins} min` : `${bars} bars`}`, swapAdvice ?? 'Swap it at the next break.');
        }
        /*
         * The one nobody else can tell you.
         *
         *
         * We hold the running order as well as the telemetry, so we can compare
         * the runtime left in the pack against how long this presenter is still
         * on. "45 minutes left" means nothing on its own; "20 minutes short of
         * when Dana comes off" is a decision.
         */
        if (mins !== undefined && live && live.scheduled) {
            const needed = live.end - input.now;
            if (needed > 0 && mins < needed) {
                /*
                 * Say when, not just that. The channel's own gaps are the moments this
                 * pack is off a body, so the answer is already in the plot.
                 */
                push('battery-short-of-call', 'critical', `${nameFor}: ${mins} min of battery but on until ${minutesToHHMM(live.end)}`
                    + ` — ${needed - mins} min short`, 'No break before it runs out — swap it now, or move them to a spare channel.');
            }
        }
        // -- RF ----------------------------------------------------------------
        if (r.rfLevel !== undefined && r.rfLevel <= th.rfLowDbm) {
            push('rf-low', bump('warning'), `${nameFor}: RF ${r.rfLevel} dBm — check the pack's aerial and the antenna path`);
        }
        /*
         * Dropouts and a dead aerial are patterns, not readings: neither is visible
         * in the instant the meter shows. Both come from what the server has been
         * watching happen.
         */
        if (hist && hist.dropouts >= th.dropoutsWorthMentioning) {
            push('rf-dropouts', bump('warning'), `${nameFor}: ${hist.dropouts} RF dropouts so far`
                + (hist.minRfLevel !== undefined ? `, worst ${hist.minRfLevel} dBm` : ''), 'Look at the antenna path and the pack aerial before it happens on a line.');
        }
        if (hist && hist.samples >= th.antennaMinSamples) {
            const total = hist.antennaA + hist.antennaB;
            if (total > 0) {
                const share = Math.min(hist.antennaA, hist.antennaB) / total;
                if (share < th.antennaImbalanceRatio) {
                    const dead = hist.antennaA < hist.antennaB ? 'A' : 'B';
                    push('antenna-imbalance', bump('warning'), `${nameFor}: antenna ${dead} has barely been used — diversity may be down to one aerial`, `Check the ${dead} cable, its connector and any inline amplifier. `
                        + 'One working aerial holds up until someone stands in the wrong place.');
                }
            }
        }
        /*
         * -- audio ------------------------------------------------------------
         *
         * Against `onAir`, not `live`. An assignment with no times means the mic is
         * that person's for the whole show, which is every assignment in
         * run-and-gun -- the default mode. Testing only the scheduled case meant a
         * muted pack and a dead element went unreported on the shows most likely to
         * be run without a schedule at all.
         */
        if (onAir) {
            if (r.muted) {
                push('muted-on-air', 'critical', `${nameFor}: transmitter is MUTED and they are on`, (offSchedule
                    ? 'The running order says they are off, but the pack is transmitting. '
                    : '')
                    + 'Unmute at the pack, or check whether somebody muted it at the receiver.');
            }
            else if (r.audioLevel !== undefined && r.audioLevel <= th.audioSilentDb) {
                push('audio-silent', 'critical', `${nameFor}: no audio while they are on — check the element and the pack`, 'Element, cable and connector at the pack, in that order.');
            }
        }
        if (r.audioLevel !== undefined && r.audioLevel >= th.audioClipDb) {
            push('audio-clipping', bump('warning'), `${nameFor}: audio at ${r.audioLevel} dB — pad the transmitter gain`);
        }
        // -- does the rig match the plot? --------------------------------------
        if (channel && r.txType && channel.micType) {
            const t = r.txType.toLowerCase();
            const looksHandheld = /hh|handheld|ksm|sm58|vocal|nex/.test(t);
            if (looksHandheld && isBodyWorn(channel.micType)) {
                push('frequency-mismatch', 'info', `${nameFor}: receiver reports a handheld but the plot says ${channel.micType}`);
            }
        }
    }
    /*
     * A channel in the plot that nothing is reporting. Only worth saying while a
     * bridge is actually feeding us -- otherwise every channel is unreceived,
     * which is true and useless.
     */
    if (input.bridgeLive) {
        for (const c of input.channels) {
            if (c.frequency === undefined)
                continue;
            if (matched.has(c.id) || heard.has(c.frequency.toFixed(3)))
                continue;
            /*
             * Held counts as on, exactly as it does for the audio checks above.
             * An assignment with no times is every assignment in run-and-gun, the
             * default mode -- so testing only the scheduled case meant losing a
             * receiver on a mic somebody was actually wearing reported as a warning,
             * folded behind a summary, without even naming who was on it.
             */
            const onAir = input.assignments.find(a => {
                if (a.channelId !== c.id)
                    return false;
                const st = liveStateOf(a, input.now);
                return st === 'live' || st === 'held';
            });
            const who = onAir ? byId.get(onAir.presenterId)?.name : undefined;
            out.push({
                kind: 'channel-not-received',
                severity: onAir ? 'critical' : 'warning',
                channelId: c.id,
                channelNumber: c.number,
                presenterName: who,
                message: `TX ${c.number}${who ? ` (${who})` : ''}: nothing is being received on `
                    + `${c.frequency.toFixed(3)} MHz`,
                advice: 'The receiver may be switched off, retuned, or off the network.',
                key: `plot:${c.id}:channel-not-received`,
            });
        }
    }
    return out.sort((a, b) => SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity]
        || (a.channelNumber ?? 0) - (b.channelNumber ?? 0));
}
/** The worst severity present, for a badge that has to be one colour. */
export function worstSeverity(alerts) {
    if (alerts.length === 0)
        return null;
    return alerts.reduce((worst, a) => SEVERITY_RANK[a.severity] < SEVERITY_RANK[worst] ? a.severity : worst, 'info');
}
